import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { SUBCATEGORIES, subCounts, subHref } from "@/lib/subcategories";

const slugOf = (to) => to.split("/").pop();
const testSlug = (label) => label.toLowerCase().replace(/&/g, "and").replace(/\s+/g, "-");

// All products of a shop category, best sellers first (cached for the session)
export function useCategoryProducts(slug, enabled = true) {
  return useQuery({
    queryKey: ["menu-category-products", slug],
    queryFn: async () => (await api.get("/products", { params: { collection: slug, sort: "featured", limit: 250 } })).data,
    enabled: enabled && !!SUBCATEGORIES[slug],
    staleTime: 10 * 60 * 1000,
  });
}

// Sub-categories with their product counts; empty ones are hidden once products load
function useVisibleSubs(slug, products) {
  const subs = subCounts(slug, products || []);
  return products ? subs.filter((s) => s.count > 0) : subs;
}

function FeaturedProduct({ product, onClose }) {
  const { addItem } = useCart();
  const navigate = useNavigate();
  const variant = product.variants?.[0];
  const add = () => {
    if ((product.variants?.length || 0) > 1) { onClose(); return navigate(`/product/${product.id}`); }
    if (!variant || variant.available === false) return toast.error("Sold out");
    addItem(product, variant, 1);
    onClose();
  };
  return (
    <div className="w-[190px] flex-shrink-0" data-testid="mega-featured">
      <p className="font-sans text-[10px] tracking-[0.2em] uppercase text-[#333333]/55 mb-2">Best seller</p>
      <Link to={`/product/${product.id}`} onClick={onClose} className="block group" data-testid="mega-featured-link">
        <div className="aspect-[4/5] rounded-xl overflow-hidden bg-[#F6F2EE]">
          {product.image && <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
        </div>
        <p className="font-serif text-[15px] leading-snug text-[#333333] mt-2.5 line-clamp-2">{product.name}</p>
        <p className="font-sans text-sm font-semibold text-[#333333] mt-0.5">${product.price?.toFixed(2)}</p>
      </Link>
      <button
        onClick={add}
        data-testid="mega-featured-add"
        className="mt-3 w-full h-10 rounded-full bg-[#111111] text-white text-[11px] tracking-[0.15em] uppercase font-sans font-medium hover:bg-[#333333] transition-colors"
      >
        Add to bag
      </button>
    </div>
  );
}

/* ---- Mega Menu: Category dropdown ---- */
export function MegaCategory({ link, onClose }) {
  const slug = slugOf(link.to);
  const { data: products } = useCategoryProducts(slug);
  const subs = useVisibleSubs(slug, products);
  const top = products?.[0];
  return (
    <div className="flex gap-8 p-7 min-w-[380px]">
      <div className="flex-1 min-w-[170px]">
        <p className="font-serif text-lg text-[#333333] mb-4">{link.label}</p>
        <div className="space-y-1">
          {subs.map((sub) => (
            <Link
              key={sub.label}
              to={subHref(slug, sub.label)}
              onClick={onClose}
              className="flex items-center justify-between gap-6 py-1.5 group"
              data-testid={`mega-sub-${testSlug(sub.label)}`}
            >
              <span className="font-sans text-sm text-[#333333]/75 group-hover:text-[#333333] transition-colors whitespace-nowrap">{sub.label}</span>
              {products && <span className="font-sans text-xs text-[#333333]/40">{sub.count}</span>}
            </Link>
          ))}
        </div>
        <Link
          to={link.to}
          onClick={onClose}
          className="inline-block mt-5 text-[11px] tracking-[0.2em] uppercase font-sans font-medium text-[#333333] border-b border-[#333333] pb-0.5 hover:text-[#DCAE96] hover:border-[#DCAE96] transition-colors"
          data-testid="mega-see-all"
        >
          See All
        </Link>
      </div>
      {top ? (
        <FeaturedProduct product={top} onClose={onClose} />
      ) : (
        <div className="w-[190px] aspect-[4/5] rounded-xl overflow-hidden bg-[#F0F0EE] flex-shrink-0">
          {link.image && <img src={link.image} alt={link.label} className="w-full h-full object-cover" />}
        </div>
      )}
    </div>
  );
}

/* ---- Mega Menu: Products dropdown ---- */
export function MegaProducts({ link, onClose }) {
  const { data: products = [] } = useQuery({
    queryKey: ["mega-products", link.label],
    queryFn: async () => {
      const { data } = await api.get("/products", { params: { collection: link.label === "New" ? "new-arrivals" : "bestsellers", limit: 6 } });
      return data;
    },
  });

  return (
    <div className="p-8 min-w-[700px]">
      <p className="font-serif text-lg text-[#333333] mb-6">{link.label === "New" ? "New Arrivals" : "Bestsellers"}</p>
      <div className="grid grid-cols-3 gap-4">
        {products.slice(0, 6).map((p) => (
          <Link key={p.id} to={`/product/${p.id}`} onClick={onClose} className="group">
            <div className="aspect-square bg-[#F0F0EE] rounded-lg overflow-hidden mb-2">
              {p.image && <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />}
            </div>
            <p className="font-sans text-xs text-[#333333] line-clamp-1">{p.name}</p>
            <p className="font-sans text-xs text-[#333333]/50">${p.price?.toFixed(2)}</p>
          </Link>
        ))}
      </div>
      <div className="mt-6 text-center">
        <Link
          to={link.to}
          onClick={onClose}
          className="inline-block px-6 py-2.5 border border-[#333333] rounded-full text-[11px] tracking-[0.15em] uppercase font-sans font-medium text-[#333333] hover:bg-[#333333] hover:text-white transition-colors"
        >
          Shop All
        </Link>
      </div>
    </div>
  );
}

/* ---- Phone menu: category row that expands to its sub-categories ---- */
export function MobileCategoryItem({ item, active, onNavigate }) {
  const slug = slugOf(item.to);
  const hasSubs = !!SUBCATEGORIES[slug];
  const [open, setOpen] = useState(false);
  const { data: products } = useCategoryProducts(slug, open);
  const subs = useVisibleSubs(slug, products);
  const linkCls = `block py-3 font-serif text-2xl transition-colors ${active ? "text-[#DCAE96]" : "text-[#333333] hover:text-[#DCAE96]"}`;

  if (!hasSubs) {
    return (
      <Link to={item.to} onClick={onNavigate} className={linkCls} data-testid={`mobile-nav-${testSlug(item.label)}`} aria-current={active ? "page" : undefined}>
        {item.label}
      </Link>
    );
  }
  return (
    <div>
      <div className="flex items-center justify-between">
        <Link to={item.to} onClick={onNavigate} className={`flex-1 ${linkCls}`} data-testid={`mobile-nav-${testSlug(item.label)}`} aria-current={active ? "page" : undefined}>
          {item.label}
        </Link>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={`${open ? "Hide" : "Show"} ${item.label} categories`}
          data-testid={`mobile-expand-${testSlug(item.label)}`}
          className="w-11 h-11 flex items-center justify-center rounded-full hover:bg-[#333333]/5 transition-colors"
        >
          <ChevronDown className={`w-5 h-5 text-[#333333] transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
            data-testid={`mobile-subs-${testSlug(item.label)}`}
          >
            <div className="pl-1 pb-3 space-y-0.5">
              {subs.map((sub) => (
                <Link
                  key={sub.label}
                  to={subHref(slug, sub.label)}
                  onClick={onNavigate}
                  className="flex items-center justify-between py-2.5 font-sans text-[15px] text-[#333333]/75 hover:text-[#333333]"
                  data-testid={`mobile-sub-${testSlug(sub.label)}`}
                >
                  {sub.label}
                  {products && <span className="text-xs text-[#333333]/40 pr-3">{sub.count}</span>}
                </Link>
              ))}
              <Link to={item.to} onClick={onNavigate} className="block py-2.5 font-sans text-[13px] tracking-[0.15em] uppercase font-medium text-[#333333]">
                See all {item.label}
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
